import { PatientSimple } from '@dreryk/egabinet-patient-frontend-api';

import { assertUnreachable, useFormController } from '@dreryk/react-common';
import { Autosuggest, StyledMaxWidth } from '@dreryk/react-components';

import { guardianTranslationKeys } from '../../../../../../../translations';
import { createPatientSearchCreator } from '../../guardianData.hooks';

import { PatientSearchFormBean, PatientSearchPatient } from './PatientSearch.types';
import { PatientSearchItem } from './PatientSearchItem';

type PatientFieldProps = {
   readonly onPatientSelected: (patient: PatientSearchPatient | null) => void;
   readonly initialPatient?: PatientSearchPatient;
   readonly visitPatientId?: number;
};

const patientItemRenderer = (item: PatientSimple): JSX.Element => <PatientSearchItem patient={item} />;

const patientLabelExtractor = (patient: PatientSimple): string => {
   const name = `${patient.lastName?.asString() ?? ''} ${patient.firstName?.asString() ?? ''}`.trim();
   if (name !== '') {
      return name;
   }

   const externalIdentifierValue = patient.externalIdentifier?.accept<string>({
      visitPesel: (pesel) => pesel.asString(),
      visitGuardianPesel: () => assertUnreachable(undefined),
      visitPassport: (passport) => passport.getValue(),
      visitNationalIdentificationNumber: (nationalIdentificationNumber) => nationalIdentificationNumber.getValue(),
   });

   if (externalIdentifierValue) {
      return externalIdentifierValue;
   }
   if (patient.phoneNumber) {
      return patient.phoneNumber.asString();
   }
   return '';
};

export const PatientSearch = (props: PatientFieldProps): JSX.Element => {
   const patientSearchFormController = useFormController<PatientSearchFormBean>({ patient: props.initialPatient });

   return (
      <StyledMaxWidth maxWidth={535}>
         <Autosuggest<PatientSimple>
            searchCreator={createPatientSearchCreator(props.visitPatientId)}
            noResultsMessage={guardianTranslationKeys.patientNotFound}
            label={guardianTranslationKeys.selectGuardianFromPatientsList}
            keyProperty={'id'}
            itemRenderer={patientItemRenderer}
            labelExtractor={patientLabelExtractor}
            controller={patientSearchFormController.register('patient', { required: true })}
            placeholder={guardianTranslationKeys.fillPatientDataForSearch}
            minChars={3}
            onSelectionChange={props.onPatientSelected}
            testId={'patientSearch'}
         />
      </StyledMaxWidth>
   );
};
